import React, { useMemo, useCallback, useEffect, useState } from "react";
import { CrewScheduleCanvas } from "src/pages/CrewSchedule/CrewScheduleCanvas";
import { Grid, Theme, useTheme, alpha, Popover, Typography } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { BaseTimelineGL } from "../../../../../SharedGL/BaseTimelineGL";
import { CrewPairingList } from "../../CrewPairingList";
import { getCrew } from "src/redux/actions/CrewSchedule/getCrew";
import {
  gotCrewAction,
  getCrewScheduleData,
  updatePairingEditor,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import {
  IPairing,
  OpenFlyingStatusType,
  pairingWindow,
  IFullPairingPosition,
  isIFullPairingPosition,
  convertToTimelinePairing,
  weekInMilliseconds,
  sortCrewByDisplayOrder,
} from "@vmosolutions/oc-interfaces";
import {
  ICrewMember,
  ICrewPairingPositionMap,
  ICrewPairingPosition,
  CrewPairingPositionGrouped,
  ICrewPairingMap,
} from "src/redux/reducers/CrewScheduleReducer";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { CrewPairingActionsList } from "./CrewPairingActionsList";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockOpenTwoToneIcon from "@mui/icons-material/LockOpenTwoTone";
import Tooltip from "@mui/material/Tooltip";
import _ from "lodash";

interface ICrewListFormProps {
  id: string;
  windowId: string;
  path: string[];
}

const getCrewOnPairing = (pairing: IPairing, crewMembers: ICrewMember[]): ICrewPairingPositionMap => {
  const crewOnPairing: ICrewPairingPositionMap = {};

  pairing.positions
    //sort the positions before creating the map
    .sort(sortCrewByDisplayOrder)
    .forEach((position: IFullPairingPosition, index: number) => {
      const crewFound = crewMembers.find((crew: ICrewMember) => crew._id === position.crew?._id);
      const val = crewFound ? crewFound : position;
      const positionStr = position.position.title;
      if (crewOnPairing[positionStr]) {
        crewOnPairing[positionStr].push({ ...val, positionIndex: index });
      } else {
        crewOnPairing[positionStr] = [
          {
            ...val,
            positionIndex: index,
          },
        ];
      }
    });
  return crewOnPairing;
};

const CrewListFormStyles = (theme: Theme): any => ({
  borderRadius: "3px",
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  "& .info": { margin: "5px", fontSize: 12, color: theme.palette.primary.contrastText },
  "& .openflying_icon": { color: theme.palette.primary.contrastText },
  width: "100%",
  padding: "4px",
});

export const CrewListForm = (props: ICrewListFormProps): JSX.Element => {
  const { id, windowId } = props;
  const crewListRef = React.useRef<HTMLDivElement>();
  const dispatch = useDispatch();
  const theme = useTheme();
  const crewNFAs = useSelector((state: IRootState) => state.crewSchedule.crewNonFlyingAssignments);
  const crewMembers: ICrewMember[] = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[id]);
  const crewScheduleFetchIntervalDays = useSelector(
    (state: IRootState) => state.UISettings.crewScheduleFetchIntervalDays
  );
  // Used to dertermine which assigned/unassigned position in the view has it's open flying controls visible (if any)
  const [openFlyingDisplayIndex, setOpenFlyingDisplayIndex] = useState<number>(-1);

  const pairingCrewPairings = useMemo(() => {
    if (!storedPairing) {
      return {};
    }
    const [start, end] = pairingWindow(storedPairing);
    const formPairing: IPairing = { ..._.cloneDeep(storedPairing), start, end };
    const pairingCrewPairingMap: ICrewPairingMap = {};
    formPairing.positions.forEach((value: IFullPairingPosition) => {
      if (!value.crew) {
        return;
      }
      const crewId = value.crew._id;
      pairingCrewPairingMap[crewId] = {
        lastModified: new Date().getTime(),
        pairings: [convertToTimelinePairing(formPairing)],
      };
    });
    return pairingCrewPairingMap;
  }, [storedPairing]);

  const crewMembersOnPairing = useMemo(() => {
    if (crewMembers && storedPairing) {
      const members = getCrewOnPairing(storedPairing, crewMembers);
      return members;
    }
    return {};
  }, [crewMembers, storedPairing]);

  const crewArr = useMemo(() => {
    const arr: ICrewPairingPosition[] = [];
    Object.keys(crewMembersOnPairing).forEach((value: string) => {
      const positions = crewMembersOnPairing[value];
      const crewPositions: ICrewPairingPosition[] = positions.map((val: CrewPairingPositionGrouped) => {
        (val as ICrewMember).isSelected = false;
        return isIFullPairingPosition(val) ? { position: val.position.title, crew_id: val.crew?._id } : { ...val };
      });

      arr.push(...crewPositions);
    });
    return arr;
  }, [crewMembersOnPairing]);

  // If position context menu returns a valid position id, find and return that position info for use in display open flying controls.
  const fullPositions: IFullPairingPosition[] = useMemo(() => storedPairing?.positions ?? [], [storedPairing]);
  const openFlyingPosition: IFullPairingPosition = useMemo(() => {
    return fullPositions[openFlyingDisplayIndex];
  }, [fullPositions, openFlyingDisplayIndex]);

  const anchorEl: Element = useMemo(() => {
    if (openFlyingDisplayIndex > -1) {
      const parentHtmlId = `${windowId}-reparentable`;
      const parent = document.getElementById(parentHtmlId);
      const htmlClass = `position_block_${openFlyingDisplayIndex}`;
      const anchor = parent.getElementsByClassName(htmlClass)[0];
      return anchor;
    }
    return null;
  }, [openFlyingDisplayIndex, id]);

  // Modify openflying value for a given position.
  const handleModifyOpenFlying = useCallback(
    (event: any, openFlyingValue: OpenFlyingStatusType) => {
      const updatedPositions = _.cloneDeep(fullPositions);
      updatedPositions[openFlyingDisplayIndex].openFlyingStatus =
        openFlyingValue !== null ? openFlyingValue : OpenFlyingStatusType.Closed;
      dispatch(updatePairingEditor(id, { positions: updatedPositions }));
    },
    [fullPositions, openFlyingDisplayIndex]
  );

  // Determine starting point for the viewport (not the pairing inside it).
  const [intervalStart, intervalEnd] = useMemo(() => {
    if (!storedPairing) {
      return [0, 0];
    }
    const [start, end] = pairingWindow(storedPairing);
    //if it is a really long pairing maybe you should set a max for the halfDiff of a week on each side
    const halfDiff = end - start > 2 * weekInMilliseconds ? weekInMilliseconds : (end - start) / 2.0;

    return [start - halfDiff, end + halfDiff];
  }, [storedPairing]);

  const getCrewAction = useCallback(async () => {
    const fetchedCrew: ICrewMember[] = crewMembers.length > 0 ? crewMembers : await getCrew();
    dispatch(gotCrewAction(fetchedCrew));
  }, [dispatch, crewMembers]);

  useEffect(() => {
    if (!crewMembers.length && !!getCrewAction) {
      getCrewAction();
    }
  }, [getCrewAction, crewMembers]);

  useEffect(() => {
    if (intervalStart && intervalEnd) {
      dispatch(getCrewScheduleData(intervalStart, intervalEnd, crewScheduleFetchIntervalDays));
    }
  }, [dispatch, intervalStart, intervalEnd, crewScheduleFetchIntervalDays]);

  // Do not attempt to display crew position information until you have a calculated view start and end.
  if (!intervalStart || !intervalEnd) {
    return null;
  }

  return (
    <Grid container direction="column">
      <Grid container className="positions">
        <Grid
          item
          className="crew_list"
          id={`${id}-crewList`}
          style={{ width: "120px", marginTop: BaseTimelineGL.timelineStart }}
        >
          <Grid container direction="column" style={{ height: "100%" }}>
            {crewMembersOnPairing && (
              <CrewPairingList
                crewMembersOnPairing={crewMembersOnPairing}
                id={id}
                setCrewListRef={(ref: React.MutableRefObject<HTMLDivElement>) => (crewListRef.current = ref.current)}
                disableMouseEvents={true}
                openFlyingDisplayIndex={openFlyingDisplayIndex}
              />
            )}
          </Grid>
        </Grid>

        <CrewScheduleCanvas
          id={id}
          path={props?.path ?? []}
          crew={crewArr}
          crewPairings={pairingCrewPairings}
          crewNFAs={crewNFAs}
          crewListRef={crewListRef}
          initialStartTime={intervalStart}
          initialEndTime={intervalEnd}
          isCameraLocked={true}
          disableEventSummary={true}
          pairingEditorView={true}
        />

        <Grid item id={`${id}-crewList`} style={{ marginTop: BaseTimelineGL.timelineStart }}>
          <Grid container direction="column" style={{ height: "100%" }}>
            <CrewPairingActionsList
              crewMembersOnPairing={crewMembersOnPairing}
              id={id}
              openFlyingDisplayIndex={openFlyingDisplayIndex}
              setOpenFlyingDisplayIndex={setOpenFlyingDisplayIndex}
            />
          </Grid>
        </Grid>
      </Grid>

      {anchorEl && (
        <Popover
          open={anchorEl ? true : false}
          onClose={() => setOpenFlyingDisplayIndex(-1)}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableRestoreFocus
        >
          <Grid container sx={CrewListFormStyles(theme)} alignItems="center" style={{ width: "100%", padding: 4 }}>
            <Typography className="info">Open Flying Status:</Typography>
            <ToggleButtonGroup
              value={openFlyingPosition?.openFlyingStatus}
              exclusive
              onChange={handleModifyOpenFlying}
              aria-label="text alignment"
            >
              <ToggleButton
                size="small"
                className="openflying_icon"
                value={OpenFlyingStatusType.Closed}
                aria-label="left aligned"
              >
                <Tooltip enterDelay={500} placement="top" title="Closed">
                  <LockIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                size="small"
                className="openflying_icon"
                value={OpenFlyingStatusType.OpenNonVisible}
                aria-label="centered"
              >
                <Tooltip enterDelay={500} placement="top" title="Open - Hidden To Crew">
                  <LockOpenIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                size="small"
                className="openflying_icon"
                value={OpenFlyingStatusType.OpenVisible}
                aria-label="right aligned"
              >
                <Tooltip enterDelay={500} placement="top" title="Open - Visible To Crew">
                  <LockOpenTwoToneIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Popover>
      )}
    </Grid>
  );
};
